import { useTranslations } from '@vocab/react';
import {
  Columns,
  Column,
  Heading,
  Stack,
  Box,
  Actions,
  ButtonLink,
  Inline,
  IconArrow,
} from 'braid-design-system';
import { useLocation, useNavigate } from 'react-router-dom';
import translations from './.vocab';
import { PhotoTiles } from '../../common/PhotoTiles/PhotoTiles';
import type { Gallery } from '../../../';
import { Section } from '../../common/section/Section';
import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react';

interface Props {
  mode: 'view' | 'edit';
  model: Gallery;
  loading?: boolean;
  link?: string;
  setActiveTab?: Dispatch<SetStateAction<string | undefined>>;
  trackSeeAllPhotosButtonClicked?: () => void;
  trackPhotosPaginationClicked?: ({
    pageNumber,
  }: {
    pageNumber: number;
  }) => void;
}

export const GallerySection = ({
  mode,
  loading,
  model,
  link,
  setActiveTab,
  trackSeeAllPhotosButtonClicked,
  trackPhotosPaginationClicked,
}: Props) => {
  const { t } = useTranslations(translations);
  const isAboutPage = Boolean(link);
  const heading = isAboutPage ? `${model.name} ${t('photos')}` : t('Gallery');
  const photos = isAboutPage ? model.photos.slice(0, 4) : model.photos;
  const navigate = useNavigate();
  const { state } = useLocation();
  const galleryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state?.shouldScrollToGallery) {
      scrollToGallery();
    }
  }, [state?.shouldScrollToGallery]);

  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const photoLinkClicked = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (setActiveTab) {
      if (trackSeeAllPhotosButtonClicked) trackSeeAllPhotosButtonClicked();
      setActiveTab('culture');
      navigate('culture', {
        state: { shouldScrollToGallery: true },
      });
    }
  };

  return (
    <Section mode={mode} loading={loading} paddingBottom="none">
      <Box ref={galleryRef}>
        <Stack space="large">
          <Columns space="none" alignY="center">
            <Column>
              <Heading level="3">{heading}</Heading>
            </Column>
          </Columns>
          <PhotoTiles
            photos={photos}
            modalTitle={heading}
            scrollToGallery={scrollToGallery}
            trackPhotosPaginationClicked={trackPhotosPaginationClicked}
          />
          {isAboutPage && (
            <Actions>
              <Inline space="none">
                <ButtonLink
                  href={link || '#'}
                  onClick={photoLinkClicked}
                  icon={<IconArrow direction="right" />}
                  iconPosition="trailing"
                >
                  {t('See all photos')}
                </ButtonLink>
              </Inline>
            </Actions>
          )}
        </Stack>
      </Box>
    </Section>
  );
};
